(function () {
  'use strict';

  var initializedShowcases = new WeakMap();

  function toArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  function initializeReviewShowcase(showcase) {
    if (!showcase || initializedShowcases.has(showcase)) {
      return;
    }

    var abortController = new AbortController();
    var signal = abortController.signal;
    var slider = showcase.querySelector('[data-review-slider]');
    var viewport = showcase.querySelector('[data-review-slider-viewport]');
    var track = showcase.querySelector('[data-review-slider-track]');
    var slides = toArray(showcase.querySelectorAll('[data-review-slide]'));
    var dots = toArray(showcase.querySelectorAll('[data-review-slider-dot]'));
    var pagination = showcase.querySelector('.product-review-slider__pagination');
    var controls = showcase.querySelector('.product-review-slider__controls');
    var previousButton = showcase.querySelector('[data-review-slider-previous]');
    var nextButton = showcase.querySelector('[data-review-slider-next]');
    var mediaMarquee = showcase.querySelector('[data-review-media-marquee]');
    var mediaTrack = showcase.querySelector('[data-review-media-track]');
    var modal = showcase.querySelector('[data-review-video-modal]');
    var modalFrame = showcase.querySelector('[data-review-video-frame]');
    var modalCloseButtons = toArray(
      showcase.querySelectorAll('[data-review-video-close]')
    );

    var currentSlide = 0;
    var visibleSlides = 1;
    var maximumStart = Math.max(0, slides.length - 1);
    var pointerStartX = null;
    var pointerStartY = null;
    var pointerCurrentX = null;
    var pointerCurrentY = null;
    var activePointerId = null;
    var activeVideoTrigger = null;
    var layoutFrame = null;
    var resizeObserver = null;
    var primaryMediaTemplates = [];
    var generatedMediaCount = 0;

    function getMediaGroups() {
      if (!mediaTrack) {
        return [];
      }

      return toArray(
        mediaTrack.querySelectorAll('.product-review-showcase__media-group')
      );
    }

    function prepareMediaClone(node) {
      var clone = node.cloneNode(true);
      var elementsWithIds = toArray(clone.querySelectorAll('[id]'));
      var editorElements = toArray(
        clone.querySelectorAll('[data-shopify-editor-block]')
      );
      var focusableElements = toArray(
        clone.querySelectorAll('button, a, input, select, textarea, [tabindex]')
      );

      clone.setAttribute('data-review-media-generated', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');

      elementsWithIds.forEach(function (element) {
        element.removeAttribute('id');
      });

      editorElements.forEach(function (element) {
        element.removeAttribute('data-shopify-editor-block');
      });

      focusableElements.forEach(function (element) {
        element.setAttribute('tabindex', '-1');
      });

      return clone;
    }

    function synchronizeMediaCloneGroup(primaryGroup, cloneGroup) {
      if (!primaryGroup || !cloneGroup) {
        return;
      }

      cloneGroup.innerHTML = '';

      toArray(primaryGroup.children).forEach(function (mediaItem) {
        cloneGroup.appendChild(prepareMediaClone(mediaItem));
      });

      cloneGroup.setAttribute('aria-hidden', 'true');
    }

    function prepareHostedVideos() {
      toArray(showcase.querySelectorAll('.product-review-media video')).forEach(
        function (video) {
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.autoplay = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');

          var playPromise = video.play();

          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {
              /* O navegador pode aguardar a mídia entrar na área visível. */
            });
          }
        }
      );
    }

    function ensureMediaCoverage() {
      var groups = getMediaGroups();

      if (!mediaMarquee || groups.length < 2) {
        return;
      }

      var primaryGroup = groups[0];
      var cloneGroup = groups[1];

      if (primaryMediaTemplates.length === 0) {
        primaryMediaTemplates = toArray(primaryGroup.children).map(function (item) {
          return item.cloneNode(true);
        });
      }

      if (primaryMediaTemplates.length === 0) {
        return;
      }

      var targetWidth = Math.max(
        mediaMarquee.clientWidth + 40,
        window.innerWidth + 40
      );
      var safetyLimit = 80;
      var additions = 0;

      while (
        primaryGroup.scrollWidth < targetWidth &&
        additions < safetyLimit
      ) {
        var template = primaryMediaTemplates[
          generatedMediaCount % primaryMediaTemplates.length
        ];

        primaryGroup.appendChild(prepareMediaClone(template));
        generatedMediaCount += 1;
        additions += 1;
      }

      if (
        additions > 0 ||
        cloneGroup.children.length !== primaryGroup.children.length
      ) {
        synchronizeMediaCloneGroup(primaryGroup, cloneGroup);
        prepareHostedVideos();
      }
    }

    function calculateVisibleSlides() {
      if (!viewport || !track || slides.length === 0) {
        return 1;
      }

      var firstSlide = slides[0];
      var slideWidth = firstSlide.getBoundingClientRect().width;
      var viewportWidth = viewport.getBoundingClientRect().width;
      var trackStyles = window.getComputedStyle(track);
      var gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

      if (slideWidth <= 0 || viewportWidth <= 0) {
        if (window.matchMedia('(max-width: 768px)').matches) {
          return 1;
        }

        var configuredColumns = parseInt(
          window.getComputedStyle(showcase).getPropertyValue(
            '--review-showcase-columns'
          ),
          10
        );

        return Math.max(1, Math.min(configuredColumns || 3, slides.length));
      }

      return Math.max(
        1,
        Math.min(
          Math.round((viewportWidth + gap) / (slideWidth + gap)),
          slides.length
        )
      );
    }

    function normalizeSlideIndex(index) {
      if (slides.length === 0 || maximumStart === 0) {
        return 0;
      }

      if (index < 0) {
        return maximumStart;
      }

      if (index > maximumStart) {
        return 0;
      }

      return index;
    }

    function setNavigationVisibility() {
      var canNavigate = slides.length > visibleSlides;

      if (pagination) {
        pagination.style.display = canNavigate ? 'flex' : 'none';
        pagination.setAttribute('aria-hidden', canNavigate ? 'false' : 'true');
      }

      if (controls) {
        controls.style.display = canNavigate ? 'flex' : 'none';
        controls.setAttribute('aria-hidden', canNavigate ? 'false' : 'true');
      }

      dots.forEach(function (dot, index) {
        var isAvailable = canNavigate && index <= maximumStart;

        dot.style.display = isAvailable ? '' : 'none';
        dot.disabled = !isAvailable;
      });
    }

    function updateSlider(options) {
      if (!track || slides.length === 0) {
        return;
      }

      var settings = options || {};

      visibleSlides = calculateVisibleSlides();
      maximumStart = Math.max(0, slides.length - visibleSlides);
      currentSlide = normalizeSlideIndex(currentSlide);

      if (settings.immediate) {
        track.style.transition = 'none';
      } else {
        track.style.removeProperty('transition');
      }

      var firstOffset = slides[0].offsetLeft;
      var targetOffset = slides[currentSlide].offsetLeft - firstOffset;

      track.style.transform =
        'translate3d(-' + Math.max(0, targetOffset) + 'px, 0, 0)';

      slides.forEach(function (slide, index) {
        var isVisible =
          index >= currentSlide && index < currentSlide + visibleSlides;

        slide.classList.toggle('is-active', isVisible);
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        if (isVisible) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });

      dots.forEach(function (dot, index) {
        var isActive = index === currentSlide;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      setNavigationVisibility();

      if (settings.immediate) {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            track.style.removeProperty('transition');
          });
        });
      }

      if (!settings.silent) {
        showcase.dispatchEvent(
          new CustomEvent('review-showcase:slide-change', {
            bubbles: true,
            detail: {
              index: currentSlide,
              visibleSlides: visibleSlides
            }
          })
        );
      }
    }

    function goToSlide(index, options) {
      currentSlide = normalizeSlideIndex(index);
      updateSlider(options);
    }

    function goToPreviousSlide() {
      goToSlide(currentSlide - 1);
    }

    function goToNextSlide() {
      goToSlide(currentSlide + 1);
    }

    function resetPointer() {
      pointerStartX = null;
      pointerStartY = null;
      pointerCurrentX = null;
      pointerCurrentY = null;
      activePointerId = null;
    }

    function finishPointerGesture() {
      if (
        pointerStartX === null ||
        pointerStartY === null ||
        pointerCurrentX === null ||
        pointerCurrentY === null
      ) {
        resetPointer();
        return;
      }

      var horizontalMovement = pointerCurrentX - pointerStartX;
      var verticalMovement = pointerCurrentY - pointerStartY;

      if (
        Math.abs(horizontalMovement) >= 45 &&
        Math.abs(horizontalMovement) > Math.abs(verticalMovement)
      ) {
        if (horizontalMovement > 0) {
          goToPreviousSlide();
        } else {
          goToNextSlide();
        }
      }

      resetPointer();
    }

    function createVideoIframe(videoType, videoId) {
      var iframe = document.createElement('iframe');
      var source = '';

      if (videoType === 'youtube') {
        source =
          'https://www.youtube-nocookie.com/embed/' +
          encodeURIComponent(videoId) +
          '?autoplay=1&rel=0';
      }

      if (videoType === 'vimeo') {
        source =
          'https://player.vimeo.com/video/' +
          encodeURIComponent(videoId) +
          '?autoplay=1&title=0&byline=0&portrait=0';
      }

      if (!source) {
        return null;
      }

      iframe.src = source;
      iframe.title = 'Customer review video';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.loading = 'eager';

      return iframe;
    }

    function openVideoModal(trigger) {
      if (!modal || !modalFrame || !trigger) {
        return;
      }

      var videoType = trigger.dataset.videoType;
      var videoId = trigger.dataset.videoId;
      var iframe = createVideoIframe(videoType, videoId);

      if (!iframe) {
        return;
      }

      activeVideoTrigger = trigger;
      modalFrame.innerHTML = '';
      modalFrame.appendChild(iframe);
      modal.hidden = false;
      document.body.classList.add('product-review-video-is-open');

      var closeButton = modal.querySelector(
        '.product-review-video-modal__close'
      );

      if (closeButton) {
        window.requestAnimationFrame(function () {
          closeButton.focus();
        });
      }

      showcase.dispatchEvent(
        new CustomEvent('review-showcase:video-open', {
          bubbles: true,
          detail: {
            type: videoType,
            id: videoId
          }
        })
      );
    }

    function closeVideoModal(options) {
      if (!modal || modal.hidden) {
        return;
      }

      var settings = options || {};

      modal.hidden = true;

      if (modalFrame) {
        modalFrame.innerHTML = '';
      }

      document.body.classList.remove('product-review-video-is-open');

      if (activeVideoTrigger && !settings.skipFocus) {
        activeVideoTrigger.focus();
      }

      activeVideoTrigger = null;

      showcase.dispatchEvent(
        new CustomEvent('review-showcase:video-close', {
          bubbles: true
        })
      );
    }

    function scheduleLayoutUpdate() {
      if (layoutFrame !== null) {
        return;
      }

      layoutFrame = window.requestAnimationFrame(function () {
        layoutFrame = null;
        ensureMediaCoverage();
        updateSlider({
          immediate: true,
          silent: true
        });
      });
    }

    if (previousButton) {
      previousButton.addEventListener('click', goToPreviousSlide, {
        signal: signal
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', goToNextSlide, {
        signal: signal
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener(
        'click',
        function () {
          goToSlide(Number(dot.dataset.reviewSliderDot));
        },
        { signal: signal }
      );
    });

    if (slider) {
      slider.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToPreviousSlide();
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToNextSlide();
          }
        },
        { signal: signal }
      );
    }

    if (viewport) {
      viewport.addEventListener(
        'pointerdown',
        function (event) {
          if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
          }

          pointerStartX = event.clientX;
          pointerStartY = event.clientY;
          pointerCurrentX = event.clientX;
          pointerCurrentY = event.clientY;
          activePointerId = event.pointerId;

          if (viewport.setPointerCapture) {
            viewport.setPointerCapture(event.pointerId);
          }
        },
        { signal: signal }
      );

      viewport.addEventListener(
        'pointermove',
        function (event) {
          if (activePointerId !== event.pointerId) {
            return;
          }

          pointerCurrentX = event.clientX;
          pointerCurrentY = event.clientY;
        },
        { signal: signal }
      );

      viewport.addEventListener(
        'pointerup',
        function (event) {
          if (activePointerId !== event.pointerId) {
            return;
          }

          finishPointerGesture();
        },
        { signal: signal }
      );

      viewport.addEventListener('pointercancel', resetPointer, {
        signal: signal
      });
    }

    showcase.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest('[data-review-video-trigger]');

        if (trigger && showcase.contains(trigger)) {
          openVideoModal(trigger);
        }
      },
      { signal: signal }
    );

    modalCloseButtons.forEach(function (button) {
      button.addEventListener('click', closeVideoModal, {
        signal: signal
      });
    });

    document.addEventListener(
      'keydown',
      function (event) {
        if (!modal || modal.hidden) {
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeVideoModal();
        }

        if (event.key === 'Tab') {
          var closeButton = modal.querySelector(
            '.product-review-video-modal__close'
          );

          if (closeButton) {
            event.preventDefault();
            closeButton.focus();
          }
        }
      },
      { signal: signal }
    );

    showcase.addEventListener(
      'shopify:block:select',
      function (event) {
        var selectedSlide = event.target.closest('[data-review-slide]');

        if (!selectedSlide) {
          return;
        }

        var selectedIndex = slides.indexOf(selectedSlide);

        if (selectedIndex < 0) {
          return;
        }

        if (
          selectedIndex < currentSlide ||
          selectedIndex >= currentSlide + visibleSlides
        ) {
          goToSlide(Math.min(selectedIndex, maximumStart), {
            immediate: true
          });
        }
      },
      { signal: signal }
    );

    if ('ResizeObserver' in window && (viewport || mediaMarquee)) {
      resizeObserver = new ResizeObserver(scheduleLayoutUpdate);

      if (viewport) {
        resizeObserver.observe(viewport);
      }

      if (mediaMarquee) {
        resizeObserver.observe(mediaMarquee);
      }
    } else {
      window.addEventListener('resize', scheduleLayoutUpdate, {
        signal: signal
      });
    }

    primaryMediaTemplates = getMediaGroups()[0]
      ? toArray(getMediaGroups()[0].children).map(function (item) {
          return item.cloneNode(true);
        })
      : [];

    ensureMediaCoverage();
    prepareHostedVideos();
    updateSlider({
      immediate: true,
      silent: true
    });

    initializedShowcases.set(showcase, {
      destroy: function () {
        closeVideoModal({ skipFocus: true });

        if (layoutFrame !== null) {
          window.cancelAnimationFrame(layoutFrame);
          layoutFrame = null;
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
        }

        abortController.abort();
        initializedShowcases.delete(showcase);
      }
    });
  }

  function collectReviewShowcases(container) {
    var scope = container || document;
    var showcases = [];

    if (
      scope.nodeType === 1 &&
      scope.matches &&
      scope.matches('[data-product-review-showcase]')
    ) {
      showcases.push(scope);
    }

    if (scope.querySelectorAll) {
      showcases = showcases.concat(
        toArray(scope.querySelectorAll('[data-product-review-showcase]'))
      );
    }

    return showcases;
  }

  function initializeAllReviewShowcases(container) {
    collectReviewShowcases(container).forEach(function (showcase) {
      initializeReviewShowcase(showcase);
    });
  }

  function destroyReviewShowcases(container) {
    collectReviewShowcases(container).forEach(function (showcase) {
      var instance = initializedShowcases.get(showcase);

      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
  }

  function startReviewShowcases() {
    initializeAllReviewShowcases(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startReviewShowcases, {
      once: true
    });
  } else {
    startReviewShowcases();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAllReviewShowcases(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyReviewShowcases(event.target);
  });
})();
